import { authenticatedFastApiAxiosInstance } from "@/lib/data/http";
import { baseFastApiMatterRoute } from "@/lib/data/requests/matters/matter";
import {
  type APIResponse,
  type SourceOwnedConnectionCreate,
} from "@/lib/types";
import type { MatterVehicleData } from "./../../../types/Matter";

const matterVehiclePayloadKeys = [
  "color",
  "damageSeverity",
  "dateModified",
  "deductiblePaidAmount",
  "deductibleReimbursed",
  "description",
  "id",
  "insuranceId",
  "licensePlateNumber",
  "lossOfUse",
  "lossOfUseAmount",
  "lossOfUseDetails",
  "lossOfUseReimbursed",
  "make",
  "matterId",
  "mileage",
  "model",
  "registrationState",
  "repairEstimate",
  "totalLoss",
  "vin",
  "year",
] as const;

const getMatterVehiclePayload = (
  data: Partial<MatterVehicleData>,
): Partial<MatterVehicleData> =>
  Object.fromEntries(
    matterVehiclePayloadKeys
      .filter((key) => key in data)
      .map((key) => [key, data[key]]),
  ) as Partial<MatterVehicleData>;

export type CreateMatterVehicleV3Input = Pick<MatterVehicleData, "matterId"> &
  Partial<MatterVehicleData> & {
    connections?: SourceOwnedConnectionCreate[];
  };

export type VehicleRelatedMatterPlacement = {
  matterId: string;
  placement: "driver" | "passenger" | "other_party";
};

export type CopyPartyVehicleToRelatedMattersInput = {
  vehicleId: string;
  placements: VehicleRelatedMatterPlacement[];
  sourceMatterContactConnectionId: string;
  sourceMatterId: string;
};

export const createMatterVehicleV3 = (data: CreateMatterVehicleV3Input) => {
  const { connections = [], ...vehicleData } = data;
  return authenticatedFastApiAxiosInstance
    .post<APIResponse<MatterVehicleData>>("matters/v4/vehicles", {
      ...getMatterVehiclePayload(vehicleData),
      connections,
    })
    .then((res) => res.data);
};

export const copyPartyVehicleToRelatedMatters = ({
  vehicleId,
  ...data
}: CopyPartyVehicleToRelatedMattersInput) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<MatterVehicleData>
    >(`matters/v4/vehicles/${encodeURIComponent(vehicleId)}/link`, data)
    .then((res) => res.data);

export const updateMatterVehicleV1 = (
  data: Pick<MatterVehicleData, "id" | "dateModified"> &
    Partial<MatterVehicleData>,
) => {
  return authenticatedFastApiAxiosInstance
    .patch<
      APIResponse<MatterVehicleData>
    >(`${baseFastApiMatterRoute}/vehicles`, getMatterVehiclePayload(data))
    .then((res) => res.data);
};

export const deleteMatterVehicleV1 = (
  data: Pick<MatterVehicleData, "id" | "matterId">,
) => {
  const { id } = data;
  return authenticatedFastApiAxiosInstance
    .delete<
      APIResponse<MatterVehicleData>
    >(`${baseFastApiMatterRoute}/vehicles/${id}`)
    .then((res) => res.data);
};
